import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ScrollView, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { cn } from '@/utils/cn';

export interface WizardStep {
  label: string;
  /** Resumo do que já foi escolhido nesse passo. */
  value?: string;
}

interface Props {
  steps: WizardStep[];
  /** Passo atual, começando em 1. */
  current: number;
  onSelect: (step: number) => void;
}

/**
 * Trilha do assistente: fita de passos no topo, sempre visível.
 *
 * Serve de mapa e de atalho — o prestador vê quantas perguntas faltam e volta
 * para qualquer uma sem perder o que já preencheu.
 */
export function WizardTrack({ steps, current, onSelect }: Props) {
  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: 6, paddingHorizontal: 2, paddingVertical: 2 }}
    >
      {steps.map((step, index) => {
        const number = index + 1;
        const active = number === current;
        const done = number < current;

        return (
          <PressableScale
            key={step.label}
            haptic
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => onSelect(number)}
            className={cn(
              'h-9 flex-row items-center gap-2 rounded-full px-3',
              active ? 'bg-ink' : 'bg-card',
            )}
            style={{ borderCurve: 'continuous' }}
          >
            <View
              className={cn(
                'h-5 w-5 items-center justify-center rounded-full',
                active ? 'bg-primary' : done ? 'bg-success-600' : 'bg-neutral-100',
              )}
            >
              {done && !active ? (
                <Ionicons name="checkmark" size={12} color="#FFFFFF" />
              ) : (
                <ThemedText
                  className={cn(
                    'font-bold text-[10px]',
                    active ? 'text-white' : 'text-neutral-500',
                  )}
                >
                  {number}
                </ThemedText>
              )}
            </View>
            <ThemedText
              className={cn(
                'font-semibold text-[13px]',
                active ? 'text-white' : 'text-neutral-600',
              )}
            >
              {step.label}
            </ThemedText>
          </PressableScale>
        );
      })}
    </ScrollView>
  );
}
